import React, { useMemo, useState, useCallback, useRef, useId } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Lazy, FreeMode, Navigation, Thumbs } from 'swiper';
import { Tooltip } from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import BlurredBackdrop from '@components/blurred-backdrop';
import { useIsMobile } from '@utils/hooks/useIsMobile';

import './style.scss';
import 'swiper/css';
import 'swiper/css/lazy';
import "swiper/css/effect-fade";
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

const BlurredImageSlider = ({
    className = '',
    images = [],
    sizes = '100vw',
    withNavigation = false,
    loadPrevNext = false,
    thumbsPosition = "top",
    autoPlay = false,
    nested = false,
    slidesPerView = 1,
    spaceBetween = 0,
    variant = ''
}) => {
    const uid = useId();
    const tooltipId = `slide-counter-blurred-${uid}`;
    const tooltipSingleId = `slide-counter-blurred-single-${uid}`;
    const isMobile = useIsMobile();
    const thumbSpaceBetween = isMobile ? 8 : 16;
    const effectiveVariant = isMobile && variant === 'gallery-thumbs-right' ? 'gallery-no-thumbs' : variant;
    const showNavigation = withNavigation && images?.length > 1;
    const [loadedImages, setLoadedImages] = useState(new Set());
    const swiperRef = useRef();
    const thumbsSwiperRef = useRef(null);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [storedVariant, setStoredVariant] = useState(effectiveVariant);

    if (storedVariant !== effectiveVariant) {
        setStoredVariant(effectiveVariant);
        setThumbsSwiper(null);
    }

    const swiperKey = useMemo(() => `swiper-blurred-${effectiveVariant}`, [effectiveVariant]);

    const handleImageLoad = useCallback((imageIndex) => {
        setLoadedImages(prev => new Set([...prev, imageIndex]));
    }, []);

    const onSwiper = useCallback((s) => {
        swiperRef.current = s;
    }, []);

    const handleSetThumbsSwiper = useCallback((swiper) => {
        thumbsSwiperRef.current = swiper;
        setThumbsSwiper(swiper);
    }, []);

    const handleSlideChange = useCallback((swiper) => {
        const newIndex = swiper.activeIndex;
        setActiveIndex(newIndex);
        setTimeout(() => {
            if (thumbsSwiperRef.current && !thumbsSwiperRef.current.destroyed) {
                thumbsSwiperRef.current.slideTo(newIndex);
            }
        }, 0);
    }, []);

    const getSwiper = useMemo(() => {
        const variantClass = effectiveVariant ? `variant-${effectiveVariant}` : '';
        return (
            <div key={swiperKey} className={`swiper-container ${slidesPerView > 1 ? 'mutiple-slides-per-view' : ''} ${variantClass}`}>
                <Swiper
                    autoplay={{
                        enabled: autoPlay && images.length > 1,
                        delay: 5000
                    }}
                    className={`slider ${className} blurred-backdrop`}
                    effect={slidesPerView > 1 ? "slide" : "fade"}
                    initialSlide={0}
                    lazy={{
                        checkInView: false,
                        enabled: false,
                        loadPrevNext: loadPrevNext,
                    }}
                    loop={false}
                    modules={[Autoplay, EffectFade, Lazy, FreeMode, Navigation, Thumbs]}
                    onSwiper={onSwiper}
                    onSlideChange={handleSlideChange}
                    navigation={showNavigation ? { prevEl: '.gallery-nav-prev', nextEl: '.gallery-nav-next' } : false}
                    thumbs={showNavigation ? { swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null } : undefined}
                    preloadImages={true}
                    slidesPerView={slidesPerView}
                    spaceBetween={spaceBetween}
                    breakpoints={{
                        896: {
                            slidesPerView: slidesPerView > 1 ? 2.5 : 1,
                        },
                        1280: {
                            slidesPerView: slidesPerView
                        }
                    }}
                    speed={800}
                    runCallbacksOnInit={true}
                    nested={nested}
                >
                    {images?.map((image, i) => {
                        const isImageLoaded = loadedImages.has(i);

                        return (
                            <SwiperSlide key={`blurred-${i}`}>
                                {image && (
                                    <BlurredBackdrop
                                        image={image}
                                        sizes={sizes}
                                        customClass=""
                                        onImageLoad={() => handleImageLoad(i)}
                                    />
                                )}
                                {!isImageLoaded && <div className="swiper-lazy-preloader"></div>}
                            </SwiperSlide>
                        );
                    })}
                    {showNavigation && (
                        <>
                            <button type="button" className="gallery-nav-prev swiper-navigation-button swiper-navigation-button-prev" aria-label="Previous slide">
                                <i><FontAwesomeIcon icon={faChevronLeft} /></i>
                            </button>
                            <button type="button" className="gallery-nav-next swiper-navigation-button swiper-navigation-button-next" aria-label="Next slide">
                                <i><FontAwesomeIcon icon={faChevronRight} /></i>
                            </button>
                        </>
                    )}
                </Swiper>
                {(effectiveVariant === 'gallery-thumbs-below' || effectiveVariant === 'gallery-no-thumbs' || effectiveVariant === 'gallery-thumbs-right') && images?.length > 1 && (
                    <div className="slide-counter" data-tooltip-id={tooltipId}>
                        {images[activeIndex] && (images[activeIndex].imageLabel || images[activeIndex].imageDesc || images[activeIndex].imageCredit) && (
                            <span className="slide-counter-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        )}
                        {activeIndex + 1} of {images.length}
                    </div>
                )}
                {(effectiveVariant === 'gallery-thumbs-below' || effectiveVariant === 'gallery-no-thumbs' || effectiveVariant === 'gallery-thumbs-right') && images?.length > 1 && images[activeIndex] && (images[activeIndex].imageLabel || images[activeIndex].imageDesc || images[activeIndex].imageCredit) && (
                    <Tooltip id={tooltipId} className="tooltip-content" place="left">
                        <div>
                            {images[activeIndex].imageLabel && (
                                <p className="mb-[8px] bold">
                                    {images[activeIndex].imageLabel}
                                </p>
                            )}
                            {images[activeIndex].imageDesc && (
                                <p className="mb-[8px]">{images[activeIndex].imageDesc}</p>
                            )}
                            {images[activeIndex].imageCredit && (
                                <p className="m-0">Credit: {images[activeIndex].imageCredit}</p>
                            )}
                        </div>
                    </Tooltip>
                )}
                {(effectiveVariant === 'gallery-thumbs-below' || effectiveVariant === 'gallery-no-thumbs' || effectiveVariant === 'gallery-thumbs-right') && images?.length === 1 && images[0] && (images[0].imageLabel || images[0].imageDesc || images[0].imageCredit) && (
                    <>
                        <div className="slide-counter icon-only" data-tooltip-id={tooltipSingleId}>
                            <span className="slide-counter-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        </div>
                        <Tooltip id={tooltipSingleId} className="tooltip-content" place="left">
                            <div>
                                {images[0].imageLabel && <p className="mb-[8px] bold">{images[0].imageLabel}</p>}
                                {images[0].imageDesc && <p className="mb-[8px]">{images[0].imageDesc}</p>}
                                {images[0].imageCredit && <p className="m-0">Credit: {images[0].imageCredit}</p>}
                            </div>
                        </Tooltip>
                    </>
                )}
            </div>
        );
    }, [images, sizes, onSwiper, loadPrevNext, className, autoPlay, nested, slidesPerView, spaceBetween, swiperKey, loadedImages, handleImageLoad, effectiveVariant, showNavigation, thumbsSwiper, activeIndex, handleSlideChange, tooltipId, tooltipSingleId]);

    if (effectiveVariant === 'gallery-thumbs-right' && showNavigation) {
        return (
            <div className="gallery-wrapper variant-gallery-thumbs-right">
                {getSwiper}
                <Swiper
                    onSwiper={handleSetThumbsSwiper}
                    loop={false}
                    spaceBetween={thumbSpaceBetween}
                    slidesPerView="auto"
                    freeMode={true}
                    watchSlidesProgress={true}
                    centeredSlides={false}
                    slideToClickedSlide={true}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className="swiper-thumbs"
                    direction="vertical"
                >
                    {images?.map((image, i) => {
                        return (
                            <SwiperSlide key={`thumb-${i}`}>
                                <BlurredBackdrop image={image} />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
        );
    }

    return (
        <>
            {getSwiper}
            {showNavigation && effectiveVariant !== 'gallery-no-thumbs' && (
                <Swiper
                    onSwiper={handleSetThumbsSwiper}
                    loop={false}
                    spaceBetween={thumbSpaceBetween}
                    slidesPerView="auto"
                    freeMode={true}
                    watchSlidesProgress={true}
                    slideToClickedSlide={true}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className="swiper-thumbs"
                >
                    {images?.map((image, i) => {
                        return (
                            <SwiperSlide key={`thumb-${i}`}>
                                <BlurredBackdrop image={image} />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            )}
        </>
    );
};

export default BlurredImageSlider; 